<template>
  <v-card outlined class="card-center">
    <v-card-text>
      <v-container class="px-1 py-1" fluid>
        <validation-observer v-slot="{ invalid }">
          <v-row dense>
            <v-col cols="3">
              <TextFieldRef v-if="inspIndexNo != ''" :label="$t('ELE0137')" :value="inspIndexNo" />
              <TextFieldInput
                v-else
                :label="$t('ELE0137')"
                v-model="importInfo.inspIndexNo"
                validationMode="lazy"
                rules="halfWidth|inspIndexNoValidation"
                :debounce="500"
                required
              />
            </v-col>
            <v-col cols="3">
              <InspTimingDtlInput
                v-model="importInfo.inspTimingDtlCd"
                :companyCatCd="companyCatCd"
                :inspTimingCd="inspIndexInfo.inspTimingCd"
                :inspClassCd="enumInspClassCd.DIM"
                :inspCat2Cd="inspIndexInfo.inspCat2"
                :disabled="!isSearchedIndex"
                required
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <TextFieldRef :label="$t('ELE0031')" v-model="inspIndexInfo.partCd" />
            </v-col>
            <v-col cols="6">
              <TextFieldRef :label="$t('ELE0032')" v-model="inspIndexInfo.partName" />
            </v-col>
            <v-col>
              <TextFieldRef :label="$t('ELE0049')" v-model="inspIndexInfo.lotNo" />
            </v-col>
          </v-row>
          <v-row dense class="pt-6 pb-3">
            <v-col>
              <DatetimePicker v-model="importInfo.inspStartDatetime" disableFutureDatetime required />
            </v-col>
            <v-col cols="3">
              <TextFieldInputWithDatePicker
                disableFutureDate
                :label="$t('ELE0138')"
                v-model="importInfo.inspDate"
                required
              />
            </v-col>
            <v-col cols="3">
              <InspectorInput v-model="importInfo.inspector" required validOnly />
            </v-col>
          </v-row>
          <v-row dense class="pt-3">
            <v-col cols="3">
              <FileInput :label="$t('ELE0208')" multiple v-model="files" required />
            </v-col>
            <v-col cols="2">
              <TextFieldInput
                :label="$t('ELE0175')"
                v-model="importInfo.inspQty"
                required
                :rules="`rangeNumber:1,${maxQtyLimit}`"
              />
            </v-col>
            <v-spacer />
            <v-col class="mt-1 d-flex justify-end">
              <BtnCsvDownload :label="$t('ELE0076')" :fileTitle="csvFileTitle" :items="csvContents" />
            </v-col>
            <v-col class="mt-1 d-flex justify-end">
              <v-badge
                bordered
                color="error"
                icon="mdi-exclamation-thick"
                overlap
                :value="canSelectSa && !isConfirmedSa"
              >
                <Btn :label="$t('ELE0383')" :disabled="!canSelectSa" @click="selectSaDialog = true" />
              </v-badge>
            </v-col>
            <v-col class="mt-1 d-flex justify-end">
              <BtnImport @click="onClickImport" :disabled="invalid" />
            </v-col>
          </v-row>
        </validation-observer>
        <v-row v-if="isImported">
          <v-col>
            <v-card outlined>
              <v-card-title>
                {{ $t('ELE0077') }}
              </v-card-title>
              <v-card v-if="isSuccessImported" outlined class="mx-auto" color="green lighten-5" width="600px">
                <v-card-title>
                  <v-row>
                    <v-col class="d-flex flex-row space-between">
                      <span class="mx-3">
                        <v-avatar color="green" size="36">
                          <v-icon color="white">mdi-check-bold</v-icon>
                        </v-avatar>
                      </span>
                      <span class="pt-1">
                        {{ $t('MSG0038', { p0: successLineCount }) }}
                      </span>
                    </v-col>
                  </v-row>
                </v-card-title>
              </v-card>
              <v-card v-if="errorList.length > 0" outlined class="mx-auto" color="red lighten-5" width="600px">
                <v-card-title class="text-h5 justify-center">
                  {{ $t('MSG0039') }}
                </v-card-title>
                <v-card-text class="text-h6 font-weight-normal black--text">
                  <v-row>
                    <v-col class="d-flex flex-row">
                      <span class="mx-3">
                        <v-avatar color="red" size="36" class="px-2">
                          <v-icon color="white">mdi-exclamation-thick</v-icon>
                        </v-avatar>
                      </span>
                      <span class="pt-1">{{ $t('MSG0040', { p0: errorLineCount }) }}</span>
                      <v-spacer />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              <v-card-text>
                <v-data-table v-if="errorList.length > 0" dense :headers="headers" :items="errorList">
                  <template v-slot:item.detail="{ item }">
                    <div class="py-1" style="white-space: pre">{{ item.detail }}</div>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <SelectSaDialog :dialog.sync="selectSaDialog" :propsItems="saItems" @close="isConfirmedSa = true" @set="applySa" />
  </v-card>
</template>

<script>
import moment from 'moment'
import { extend, ValidationObserver } from 'vee-validate'
import { API } from '@/plugins/api'
import { InspClassCd } from '@/enum'

import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtil from '@/mixin/date-utils.js'

import Btn from '@/components/atoms/base/button/Btn'
import BtnBack from '@/components/atoms/button/BtnBack'
import BtnImport from '@/components/atoms/button/BtnImport'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'
import FileInput from '@/components/atoms/base/file/FileInput'
import InspectorInput from '@/components/atoms/select/InspectorInput'
import DatetimePicker from '@/components/atoms/text-field/DatetimePicker'
import BtnCsvDownload from '@/components/atoms/button/BtnCsvDownload'
import SelectSaDialog from '@/components/organisms/terminal/dialog/SelectSaDialog'
import InspTimingDtlInput from '@/components/atoms/select/InspTimingDtlInput'

/** 最大測定回数 */
const MAX_NUM_OF = 13

export default {
  name: 'ImpInspDataForm',

  mixins: [APIUtils, Message, Loading, DateUtil],

  components: {
    ValidationObserver,
    Btn,
    BtnBack,
    BtnImport,
    TextFieldInput,
    TextFieldRef,
    TextFieldInputWithDatePicker,
    FileInput,
    InspectorInput,
    DatetimePicker,
    BtnCsvDownload,
    SelectSaDialog,
    InspTimingDtlInput,
  },

  props: {
    inspIndexNo: {
      type: String,
      required: false,
      default: '',
    },
    inspStartDatetime: {
      type: String,
      required: false,
      default: '',
    },
    lotNo: {
      type: String,
      required: false,
      default: '',
    },
    partCd: {
      type: String,
      required: false,
      default: '',
    },
    partName: {
      type: String,
      required: false,
      default: '',
    },
    inspCat2: {
      type: String,
      required: false,
      default: '',
    },
    inspTimingCd: {
      type: String,
      required: false,
      default: '',
    },
    versionNo: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  data() {
    return {
      isSearchedIndex: false,
      companyCatCd: this.$store.getters.companyCatCd,
      importInfo: {
        inspIndexNo: '',
        inspDate: moment().format('YYYY-MM-DD'),
        inspector: this.$store.getters.userCd,
        inspStartDatetime: moment().format('YYYY-MM-DD HH:mm:00'),
        inspQty: '',
        inspTimingDtlCd: '',
        versionNo: 0,
      },
      inspIndexInfo: {
        lotNo: '',
        partCd: '',
        partName: '',
        inspCat2: '',
        inspTimingCd: '',
      },
      hasCl: false,
      files: null,
      parseFiles: [],
      fileNames: [],
      importFileErrorExists: false,
      errorLineCount: null,
      successLineCount: null,
      headers: [
        { text: this.$t('ELE0209'), value: 'fileName', divider: true, width: '270' },
        { text: this.$t('ELE0084'), value: 'line', divider: true, width: '130', align: 'end' },
        { text: this.$t('ELE0085'), value: 'detail' },
      ],
      errorList: [],
      convItems: [],
      isImported: false,
      isSuccessImported: false,
      maxQtyLimit: 0,
      selectSaDialog: false,
      isConfirmedSa: true,
      saItems: [],
    }
  },

  computed: {
    csvFileTitle: {
      get() {
        let result = 'insp_dim'
        if (this.inspIndexInfo.partCd != '') {
          result = result + `_${this.inspIndexInfo.partCd}_${this.inspIndexInfo.lotNo}`
        }
        return result
      },
    },
    enumInspClassCd: () => InspClassCd,
    csvHeaderLength: {
      get() {
        let result = 10
        if (this.hasCl) {
          result += 2
        }
        return result
      },
    },
    csvContents: {
      get() {
        let result = []
        let item = {}
        item[this.$t('ELE0157')] = ''
        item[this.$t('ELE0098')] = ''
        item[this.$t('ELE0091')] = ''
        item[this.$t('ELE0092')] = ''

        item[this.$t('ELE0095')] = ''
        item[this.$t('ELE0096')] = ''
        item[this.$t('ELE0097')] = ''
        if (this.hasCl) {
          item[this.$t('ELE0538')] = ''
          item[this.$t('ELE0539')] = ''
        }
        item[this.$t('ELE0273')] = ''
        item[this.$t('ELE0274')] = ''
        item[this.$t('ELE0275')] = ''

        for (let num = 1; num <= MAX_NUM_OF; num++) {
          item[this.$t('ELE0202') + num] = ''
        }

        item[this.$t('ELE0025')] = ''

        result.push(item)

        return result
      },
    },
    canSelectSa: {
      get() {
        let result = false
        if (this.saItems.length > 0) {
          result = true
        }
        return result
      },
    },
    selectedSaNoList: {
      get() {
        let saNolist = []
        this.saItems.forEach((el) => {
          if (el.isSelected) {
            saNolist.push(el.saNo)
          }
        })
        return saNolist
      },
    },
  },

  methods: {
    async onClickImport() {
      this.isSuccessImported = false
      this.importFileErrorExists = false
      await this.csvToJson(this.files)

      if (!this.importFileErrorExists) {
        await this.checkInspData(this.convItems)
      } else {
        this.setErrorMessage(this.$t('MSG0028'))
      }

      this.isImported = true
      this.files = null
    },
    async checkInspData(data) {
      try {
        this.startLoading()
        const body = {
          inspIndexNo: this.importInfo.inspIndexNo,
          inspStartDatetime: this.toServerTime(this.importInfo.inspStartDatetime),
          importData: data,
          saNoList: this.selectedSaNoList,
        }
        const elements = await API.post('/inspDim/files/check', body)
        this.errorList = []
        if (elements.errorExists) {
          this.errorLineCount = elements.errorLineCount
          elements.errorMessages.forEach((element) => {
            let item = {}
            item.fileName = element['fileName']
            item.line = element['lineNo'] + 1
            item.detail = element.bodies.join('\r\n')
            this.errorList.push(item)
          })
        } else {
          this.successLineCount = elements.successLineCount
          await this.registerInspData(data)
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0005', { p0: this.$t('ELE0206') })))
      } finally {
        this.stopLoading()
      }
    },
    async registerInspData(data) {
      try {
        const body = {
          inspIndexNo: this.importInfo.inspIndexNo,
          inspDate: this.importInfo.inspDate,
          inspStartDatetime: this.toServerTime(this.importInfo.inspStartDatetime),
          inspector: this.importInfo.inspector,
          inspQty: this.importInfo.inspQty,
          inspTimingDtlCd: this.importInfo.inspTimingDtlCd,
          importData: data,
          indexVersionNo: this.importInfo.versionNo,
          saNoList: this.selectedSaNoList,
        }
        await API.post('/inspDim/files/register', body)
        this.isSuccessImported = true
        this.saItems = []
        this.inspIndexInfo.lotNo = ''
        this.inspIndexInfo.partCd = ''
        this.inspIndexInfo.partName = ''
        this.setSuccessMessage(this.$t('MSG0075'))
        this.resetForm()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0206') })))
      }
    },
    resetForm() {
      this.importInfo.inspIndexNo = ''
      this.importInfo.versionNo = ''
      this.importInfo.inspQty = ''
      this.$emit('reset')
    },
    async csvToJson(files) {
      let filesData = []
      await Promise.all(
        files.map(
          (file) =>
            new Promise((resolve, reject) =>
              this.$papa.parse(file, {
                skipEmptyLines: true,
                complete: resolve,
                error: reject,
              }),
            ),
        ),
      ).then((results) => {
        results.forEach((result) => {
          filesData.push(result.data)
        })
        this.convertItems(filesData)
      })
    },
    convertItems(parseData) {
      let items = []
      if (parseData.length < 1 || parseData[0].length <= 1) {
        this.importFileErrorExists = true
      } else {
        parseData.forEach((parseData, fileIndex) => {
          // 末尾追加分は数をチェックしない
          if (parseData[0].length < this.csvHeaderLength + 1) {
            this.importFileErrorExists = true
          }
          for (let index = 1; index <= parseData.length - 1; index++) {
            let item = {
              fileName: this.files[fileIndex].name,
              index: index,
              cav: parseData[index][0],
              numOfInsp: parseData[index][1],
              inspCharacCd: parseData[index][2],
              inspCharacPoint: parseData[index][3],
              values: [],
            }
            // 入力されている検査数分取り込む 末尾に何か追加されていても読み込まない
            for (let i = 0; i <= Number(this.importInfo.inspQty) - 1; i++) {
              item.values.push(parseData[index][i + this.csvHeaderLength])
            }
            items.push(item)
          }
        })
      }
      this.convItems = items
    },
    async getQtyLimit() {
      try {
        const partElement = await API.get(`/master/partCds/${this.inspIndexInfo.partCd}`)
        const elements = await API.get('/inspIndex/inspIndexes/' + this.importInfo.inspIndexNo + '/lot', {
          partRev: partElement.currentPartRev,
        })
        this.maxQtyLimit = elements.aqlDetermQty
        this.hasCl = elements.hasCl
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0078') })))
      }
    },

    async setSaInInsp() {
      try {
        this.saItems = await API.get('/sa/insp', {
          inspClassCd: InspClassCd.DIM,
          inspIndexNo: this.importInfo.inspIndexNo,
        })
      } catch (error) {
        this.saItems = []
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0291') })))
      } finally {
        this.isConfirmedSa = this.saItems.length > 0 ? false : true
      }
    },

    applySa(items) {
      this.isConfirmedSa = true
      this.saItems = items
    },
  },
  async created() {
    if (this.inspIndexNo || this.inspStartDatetime || this.versionNo) {
      this.importInfo.inspIndexNo = this.inspIndexNo
      this.importInfo.inspStartDatetime = this.inspStartDatetime
        ? this.toLocalTime(this.inspStartDatetime)
        : moment().format('YYYY-MM-DD HH:mm:00')
      this.importInfo.versionNo = this.versionNo
      this.inspIndexInfo.lotNo = this.lotNo
      this.inspIndexInfo.partCd = this.partCd
      this.inspIndexInfo.partName = this.partName
      this.inspIndexInfo.inspTimingCd = this.inspTimingCd
      this.inspIndexInfo.inspCat2 = this.inspCat2
      this.startLoading()
      await this.setSaInInsp()
      await this.getQtyLimit()
      this.stopLoading()
      this.isSearchedIndex = true
    }
  },
  async mounted() {
    extend('inspIndexNoValidation', {
      validate: async (value) => {
        let result = true
        try {
          this.startLoading()
          const params = { userCompanyCd: this.$store.getters.companyCd }
          const url = `/inspIndex/inspIndexes/${value}/validate`
          const element = await API.get(url, params)
          this.importInfo.inspStartDatetime = element.inspStartDatetime
            ? this.toLocalTime(element.inspStartDatetime)
            : moment().format('YYYY-MM-DD HH:mm:00')
          this.inspIndexInfo.lotNo = element.lotNo
          this.inspIndexInfo.partCd = element.partCd
          this.inspIndexInfo.partName = element.m_part.partName
          this.inspIndexInfo.inspTimingCd = element.inspTimingCd
          this.inspIndexInfo.inspCat2 = element.inspCat2
          this.importInfo.inspTimingDtlCd = element.inspTimingDtlCd
          this.importInfo.versionNo = element.versionNo
          await this.setSaInInsp()
          await this.getQtyLimit()
          this.isSearchedIndex = true
        } catch (error) {
          this.isSearchedIndex = false
          if (error.response.status == 404) {
            result = this.$t('MSG0049', { p0: value })
          } else {
            result = this.$t('MSG0002', { p0: this.$t('ELE0118') })
          }
        } finally {
          this.stopLoading()
        }
        return result
      },
    })
  },
}
</script>
